// biome-ignore-all lint/a11y: TODO - address a11y issues later
// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
// biome-ignore-all lint/suspicious/useIterableCallbackReturn: TODO - address later
import { DndContext } from "@dnd-kit/core";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import {
  type InfiniteData,
  type InfiniteQueryObserverResult,
  useQueryClient,
} from "@tanstack/react-query";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer, type VirtualItem } from "@tanstack/react-virtual";
import { Filter, Loader2, Search, UnfoldHorizontal, X } from "lucide-react";
import type { MouseEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { toast } from "sonner";
import { ActiveFiltersDisplay } from "@/components/active-filters-display";
import { CSVExportButton } from "@/components/csv-export-button";
import { DraggableTableHeader } from "@/components/draggable-table-header";
import { EntityFilterModal } from "@/components/entity-filter-modal";
import { ExportFilenameDialog } from "@/components/export-filename-dialog";
import type { FilterValue } from "@/components/filter-popover";
import { OpportunityColumnSelector } from "@/components/opportunity-column-selector";
import ResetTableMenu from "@/components/reset-table-menu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Input } from "@/components/ui/input";
import { useSidebar } from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { SortIcon } from "@/components/ui/sort-icon";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSavedViewContextOptional } from "@/contexts/saved-view-context";
import { useExternalConnectionLinks } from "@/hooks/api/use-external-connection-links";
import { useFieldDefinitions } from "@/hooks/api/use-field-definitions";
import {
  type OpportunitiesResponse,
  type Opportunity,
  useInfiniteOpportunities,
  useOpportunitiesExport,
} from "@/hooks/api/use-opportunities";
import { useAvailableFilterFields } from "@/hooks/use-available-filter-fields";
import {
  type SelectableColumn,
  useOpportunityColumnSelection,
} from "@/hooks/use-column-selection";
import { usePaginatedCSVExport } from "@/hooks/use-csv-export";
import { useOpportunityCustomFieldSelection } from "@/hooks/use-custom-field-selection";
import { useOpportunityFilterState } from "@/hooks/use-opportunity-filter-state";
import { useOpportunityListFilters } from "@/hooks/use-opportunity-list-filters";
import { usePersistedColumnState } from "@/hooks/use-persisted-column-state";
import { useSavedViewState } from "@/hooks/use-saved-view-state";
import { useSearchState } from "@/hooks/use-search-state";
import {
  constrainedClosestCenter,
  restrictToTableBounds,
} from "@/lib/collision-detection";
import {
  createBulkColumnResizeHandler,
  createColumnResizeHandler,
  createSingleColumnResizeHandler,
} from "@/lib/column-resize-utils";
import { formatDateForFilename } from "@/lib/date-formatting";
import {
  type FilterClearingInfo,
  type FilterColumn,
  processMetricCardFilters,
} from "@/lib/filter-display-utils";
import { cn } from "@/lib/utils";
import type { CustomFieldDefinition } from "@/types/custom-fields";
import type { SortDirection, UnifiedTableColumn } from "@/types/table-column";
import {
  getOpportunityColumnDefs,
  getOpportunityColumns,
} from "./opportunity-columns";
import { OpportunityDetailsSheet } from "./opportunity-details-sheet";
import { RunAnalysesPopover } from "./run-analyses-popover";

// Static set of touchpoint metric column keys that trigger performance warnings
const TOUCHPOINT_METRIC_COLUMNS = new Set([
  "metric_touchpoint_count",
  "metric_first_touchpoint_date",
  "metric_last_touchpoint_date",
]);

interface OpportunitiesDataTableProps {
  /**
   * Optional data source to use instead of the default useInfiniteOpportunities.
   * Used for Deep Research Reports integration.
   */
  dataSource?: {
    data: InfiniteData<OpportunitiesResponse> | undefined;
    fetchNextPage: () => void;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    isLoading: boolean;
    isError: boolean;
    error: unknown;
  };
  /**
   * Optional callback when a row is clicked.
   * If provided, this will be called instead of opening the default details sheet.
   */
  onRowClick?: (opportunity: Opportunity) => void;
  /**
   * Optional complete state override for DRR integration.
   * When provided, the table will use this state instead of its internal state management.
   */
  stateOverride?: {
    columns: {
      // State
      columnVisibility: Record<string, boolean>;
      selectedCustomColumns: SelectableColumn[];
      customFieldIdsParam: string;
      availableColumns: SelectableColumn[];
      selectedColumns: SelectableColumn[];
      selectedCoreColumns: SelectableColumn[];
      // Functions
      setColumnVisibility: (visibility: Record<string, boolean>) => void;
      toggleColumn: (columnId: string) => void;
      isColumnSelected: (columnId: string) => boolean;
      clearSelection: () => void;
      resetToDefault: () => void;
      getVisibleColumnDefs: <T>(columnDefs: ColumnDef<T>[]) => ColumnDef<T>[];
      // Loading/error
      isLoading?: boolean;
      error?: Error | null;
    };
    filters: {
      activeFilters: Record<string, unknown>;
      updateFilter: (key: string, value: unknown) => void;
      clearFilter: (key: string) => void;
      clearAllFilters: () => void;
    };
    getQueryParams: (options: {
      searchTerm?: string;
      columns?: unknown[];
      limit?: number;
    }) => Record<string, unknown>;
  };
  /**
   * Optional callback when filters change.
   * Used for Deep Research Reports integration to sync filter state.
   */
  onFiltersChange?: (filters: Record<string, unknown>) => void;
  /**
   * Optional right-side toolbar actions rendered next to the column selector.
   * Used by DRR to place contextual actions adjacent to the selector.
   */
  rightToolbarActions?: React.ReactNode;
  /**
   * Optional override for the CSV export function.
   * Used for Deep Research Reports to export from a different data source.
   */
  onExportCSV?: () => Promise<Record<string, unknown>[]>;
}

export function OpportunitiesDataTable({
  dataSource,
  onRowClick: customOnRowClick,
  stateOverride,
  onFiltersChange,
  rightToolbarActions,
  onExportCSV,
}: OpportunitiesDataTableProps = {}) {
  const OPPORTUNITY_METRIC_LIMIT = 20000;

  const queryClient = useQueryClient();
  const { state, isMobile } = useSidebar();
  const savedViewContext = useSavedViewContextOptional();
  const isLoadingView = savedViewContext?.isLoadingView ?? false;
  const loadedViewId = savedViewContext?.loadedViewId;
  const { resetSpecificKeys } = useSavedViewState({
    pageType: "opportunities::list",
  });

  // State for sorting and searching - Replace useState search with sessionStorage
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: SortDirection;
  }>({
    key: "close_date",
    direction: "desc",
  });

  // Use sessionStorage for search state (tab-specific)
  const {
    searchInputValue,
    debouncedSearchQuery,
    handleSearchChange,
    clearSearch,
  } = useSearchState("opportunities::list::search");

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState<string | null>(
    null,
  );

  // Relation filters state
  const {
    accountFilters,
    touchpointFilters,
    hasAccountFilters,
    hasTouchpointFilters,
    setAccountFilters,
    setTouchpointFilters,
    clearAllFilters: clearRelationFilters,
  } = useOpportunityFilterState("opportunities::list");
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Get field definitions for relation filter display
  const { fields: accountFieldDefs = [] } = useAvailableFilterFields("account");
  const { fields: touchpointFieldDefs = [] } =
    useAvailableFilterFields("touchpoint");
  // Process relation filters for chips (parity with Person List)
  const relationFiltersProcessed = useMemo(() => {
    return processMetricCardFilters(
      accountFilters,
      { operator: "AND", filters: [] }, // no opportunity filter group on this page
      touchpointFilters,
      hasAccountFilters,
      false,
      hasTouchpointFilters,
      accountFieldDefs,
      [],
      touchpointFieldDefs,
    );
  }, [
    accountFilters,
    touchpointFilters,
    hasAccountFilters,
    hasTouchpointFilters,
    accountFieldDefs,
    touchpointFieldDefs,
  ]);
  const relationDisplayFilters = relationFiltersProcessed.displayFilters;
  const relationFilterColumns =
    relationFiltersProcessed.filterColumns as FilterColumn[];
  const handleClearRelationFilter = useCallback(
    (_key: string, clearingInfo?: FilterClearingInfo) => {
      if (!clearingInfo) return;
      if (
        clearingInfo.type === "individual" &&
        typeof clearingInfo.index === "number"
      ) {
        if (clearingInfo.filterGroupType === "account") {
          const next = {
            ...accountFilters,
            filters: accountFilters.filters.filter(
              (_, i) => i !== clearingInfo.index,
            ),
          };
          setAccountFilters(next);
        } else if (clearingInfo.filterGroupType === "touchpoint") {
          const next = {
            ...touchpointFilters,
            filters: touchpointFilters.filters.filter(
              (_, i) => i !== clearingInfo.index,
            ),
          };
          setTouchpointFilters(next);
        }
      } else if (
        clearingInfo.type === "consolidated_date_range" &&
        clearingInfo.filterGroupType === "touchpoint" &&
        clearingInfo.fieldName
      ) {
        // Remove both GTE/LTE for the consolidated field
        const next = {
          ...touchpointFilters,
          filters: touchpointFilters.filters.filter(
            (f) => "field" in f && f.field !== clearingInfo.fieldName,
          ),
        };
        setTouchpointFilters(next);
      }
    },
    [
      accountFilters,
      touchpointFilters,
      setAccountFilters,
      setTouchpointFilters,
    ],
  );

  // Hooks for data and features
  const { ref: loadMoreRef, inView } = useInView();
  const internalFilterState = useOpportunityListFilters();

  // Use stateOverride filters if provided, otherwise use internal state
  const filters =
    stateOverride?.filters.activeFilters || internalFilterState.filters;
  const getDefaultSortSettings = internalFilterState.getDefaultSortSettings;
  const getQueryParams =
    stateOverride?.getQueryParams || internalFilterState.getQueryParams;
  const hasFilters = Object.keys(filters).length > 0;

  // Wrap filter functions to call onFiltersChange when provided
  const updateFilter = useCallback(
    (key: string, value: unknown) => {
      if (stateOverride) {
        stateOverride.filters.updateFilter(key, value);
        // Don't call onFiltersChange when using stateOverride - it manages its own state
      } else {
        internalFilterState.updateFilter(key, value as FilterValue);
        // Only call onFiltersChange for non-override mode
        if (onFiltersChange) {
          const newFilters = { ...filters, [key]: value };
          onFiltersChange(newFilters);
        }
      }
    },
    [stateOverride, internalFilterState, filters, onFiltersChange],
  );

  const clearFilter = useCallback(
    (key: string) => {
      if (stateOverride) {
        stateOverride.filters.clearFilter(key);
        // Don't call onFiltersChange when using stateOverride - it manages its own state
      } else {
        internalFilterState.clearFilter(key);
        // Only call onFiltersChange for non-override mode
        if (onFiltersChange) {
          const newFilters = { ...filters };
          delete newFilters[key];
          onFiltersChange(newFilters);
        }
      }
    },
    [stateOverride, internalFilterState, filters, onFiltersChange],
  );

  const clearAllFilters = useCallback(() => {
    if (stateOverride) {
      stateOverride.filters.clearAllFilters();
      // Don't call onFiltersChange when using stateOverride - it manages its own state
    } else {
      internalFilterState.clearAllFilters();
      // Only call onFiltersChange for non-override mode
      if (onFiltersChange) {
        onFiltersChange({});
      }
    }
  }, [stateOverride, internalFilterState, onFiltersChange]);
  const { data: connections, isLoading: isLoadingConnections } =
    useExternalConnectionLinks();
  const { data: fieldDefs } = useFieldDefinitions("opportunities");
  // Always initialize hook; ignore when stateOverride provided
  const internalCustomFieldSelection = useOpportunityCustomFieldSelection();
  const fieldIdsParam = stateOverride
    ? stateOverride.columns.customFieldIdsParam
    : internalCustomFieldSelection?.fieldIdsParam || "";

  // Normalize to CustomFieldDefinition[] regardless of override/internal path
  const selectedCustomFieldDefs = useMemo<CustomFieldDefinition[]>(() => {
    if (stateOverride) {
      const cols = stateOverride.columns.selectedCustomColumns || [];
      const defs = cols
        .map((c) => c.customField)
        .filter(Boolean) as CustomFieldDefinition[];
      const map = new Map<string, CustomFieldDefinition>();
      defs.forEach((d) => map.set(d.id, d));
      return Array.from(map.values());
    }
    return internalCustomFieldSelection?.selectedFieldsList || [];
  }, [stateOverride, internalCustomFieldSelection?.selectedFieldsList]);

  // Column state management (with resizing)
  const {
    columnOrder,
    setColumnOrder,
    columnSizing,
    setColumnSizing,
    pinnedColumns,
    toggleColumnPin,
    wrappedColumns,
    setWrappedColumns,
    toggleColumnWrap,
    resetToDefaultPinned,
    dndState: { sensors, handleDragEnd },
    getInitializeColumnOrder,
  } = usePersistedColumnState("opportunities::list::columns", {
    getDefaultPinnedColumns: () => ["name"],
  });

  // Filter options
  const filterOptions = useMemo(() => {
    if (!fieldDefs) return undefined;

    const getEnum = (id: string) =>
      fieldDefs.find((f) => f.id === id && f.has_enum_values)?.enum_values ||
      [];

    return {
      stage_name: getEnum("stage_name"),
      type: getEnum("type"),
      record_type_name: getEnum("record_type_name"),
    } as const;
  }, [fieldDefs]);

  // Reset sort to defaults
  const resetSortToDefaults = useCallback(() => {
    const defaults = getDefaultSortSettings();
    setSortConfig({
      key: defaults.sortColumn,
      direction: defaults.sortDirection,
    });
  }, [getDefaultSortSettings]);

  // Handle clear all filters and reset sort - Updated for sessionStorage
  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    resetSortToDefaults();

    // Clear search state from sessionStorage
    clearSearch();
  }, [clearAllFilters, resetSortToDefaults, clearSearch]);

  // When component mounts, set sort from filters (search is now in sessionStorage)
  useEffect(() => {
    // Load sort configuration from localStorage
    if (filters.sortColumn) {
      setSortConfig({
        key: filters.sortColumn as string,
        direction: filters.sortDirection as SortDirection,
      });
    }
    // The effect should only run once on component mount, as we're initializing from localStorage
  }, [filters.sortColumn, filters.sortDirection]);

  // Keep UI sort state in sync with filters defaults when none present
  useEffect(() => {
    if (!filters.sortColumn) {
      const defaults = getDefaultSortSettings();
      setSortConfig({
        key: defaults.sortColumn,
        direction: defaults.sortDirection,
      });
    }
  }, [filters.sortColumn, getDefaultSortSettings]);

  // Sync UI sort state when filters change (e.g., after applying a Saved View)
  useEffect(() => {
    if (filters.sortColumn) {
      setSortConfig({
        key: filters.sortColumn as string,
        direction: filters.sortDirection as SortDirection,
      });
    }
  }, [filters.sortColumn, filters.sortDirection]);

  // Define columns after all handlers are defined
  const columns = useMemo<UnifiedTableColumn<Opportunity>[]>(() => {
    return getOpportunityColumns({
      filterOptions,
      connections,
      isLoadingConnections,
      customFields: selectedCustomFieldDefs,
    });
  }, [
    filterOptions,
    connections,
    isLoadingConnections,
    selectedCustomFieldDefs,
  ]);
  // Always initialize hook; ignore when stateOverride provided
  const internalColumnState = useOpportunityColumnSelection(columns);

  // Use ALL state from override OR internal (never mixed)
  const columnState = stateOverride?.columns ?? internalColumnState;
  const getVisibleColumnDefs = columnState.getVisibleColumnDefs;
  const columnVisibility = columnState.columnVisibility;
  const setColumnVisibility = columnState.setColumnVisibility;
  const newCustomFieldIdsParam = columnState.customFieldIdsParam;
  const selectedCustomColumns = columnState.selectedCustomColumns;
  const resetColumnsToDefault = columnState.resetToDefault;

  // Build column title lookup for cleanup messages
  const columnTitleLookup = useMemo(() => {
    return columns.reduce(
      (acc, col) => {
        acc[col.key.toString()] = col.title;
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [columns]);

  // Convert to TanStack columnDefs with resizing enabled
  const columnDefs = useMemo<ColumnDef<Opportunity>[]>(() => {
    const allDefs = getOpportunityColumnDefs({
      filterOptions,
      connections,
      isLoadingConnections,
      customFields: selectedCustomFieldDefs,
    });

    // Add resizing capability and filter to only show visible columns
    return getVisibleColumnDefs(
      allDefs.map((col) => ({ ...col, enableResizing: true })),
    );
  }, [
    filterOptions,
    connections,
    isLoadingConnections,
    selectedCustomFieldDefs,
    getVisibleColumnDefs,
  ]);

  // Initialize column order when columnDefs change
  useEffect(() => {
    const initializeOrder = getInitializeColumnOrder(columnDefs);
    initializeOrder();
  }, [columnDefs, getInitializeColumnOrder]);

  // Resets limited to widths only
  const resetColumnWidths = useCallback(() => {
    setColumnSizing({});
  }, [setColumnSizing]);

  // Reset column order only (preserve pinned columns at the start)
  const resetColumnOrder = useCallback(() => {
    setColumnOrder((prev) => {
      const defaultOrder = columnDefs.map((c) => c.id!);
      const pinnedFirst = prev.filter((id) => pinnedColumns.includes(id));
      const unpinnedDefault = defaultOrder.filter(
        (id) => !pinnedColumns.includes(id),
      );
      return [...pinnedFirst, ...unpinnedDefault];
    });
  }, [setColumnOrder, columnDefs, pinnedColumns]);

  // Reset pinned columns - respects saved views if loaded
  const resetPinnedColumns = useCallback(() => {
    if (loadedViewId) {
      // Reset to saved view's configuration
      resetSpecificKeys([
        "opportunities::list::columns::pinned",
        "opportunities::list::columns::pinned-original-positions",
      ]);
    } else {
      // No saved view - reset to default pinned state
      resetToDefaultPinned();
    }
  }, [loadedViewId, resetSpecificKeys, resetToDefaultPinned]);

  // Reset wrapped columns
  const resetWrappedColumns = useCallback(() => {
    setWrappedColumns([]);
  }, [setWrappedColumns]);

  // Reset sort order to defaults
  const resetSortOrder = useCallback(() => {
    const defaults = getDefaultSortSettings();
    setSortConfig({
      key: defaults.sortColumn,
      direction: defaults.sortDirection,
    });
    updateFilter("sortColumn", defaults.sortColumn);
    updateFilter("sortDirection", defaults.sortDirection);
  }, [getDefaultSortSettings, updateFilter]);

  // Comprehensive table reset function
  const handleResetTable = useCallback(() => {
    // Clear filters and sorting
    handleClearAllFilters();

    // Reset column visibility to defaults
    resetColumnsToDefault();

    // Reset column order then widths
    resetColumnOrder();
    resetColumnWidths();

    // Reset pinned columns
    resetPinnedColumns();

    // Reset wrapped columns
    resetWrappedColumns();
  }, [
    handleClearAllFilters,
    resetColumnsToDefault,
    resetColumnOrder,
    resetColumnWidths,
    resetPinnedColumns,
    resetWrappedColumns,
  ]);

  // Get query parameters for API call with deduplicated field IDs
  const queryParams = useMemo(() => {
    // Efficiently combine and deduplicate both field ID parameters
    const combinedCustomFieldIds = [
      ...(fieldIdsParam ? fieldIdsParam.split(",") : []),
      ...(newCustomFieldIdsParam ? newCustomFieldIdsParam.split(",") : []),
    ]
      .filter(Boolean) // Remove empty strings
      .filter((id, index, array) => array.indexOf(id) === index) // Deduplicate
      .join(",");

    // Get the params from the hook and ensure all values are strings
    // to match OpportunityParams type requirements
    const params = getQueryParams({
      searchTerm: debouncedSearchQuery,
      columns,
      limit: 200,
    });

    // Build a new object with all string values to match OpportunityParams type
    const stringifiedParams: Record<string, string | undefined> = {};

    // Process each key in params
    Object.entries(params).forEach(([key, value]) => {
      // Skip undefined values
      if (value === undefined) {
        stringifiedParams[key] = undefined;
        return;
      }

      // Convert arrays to comma-separated strings
      if (Array.isArray(value)) {
        stringifiedParams[key] = value.join(",");
      }
      // Convert null to undefined (not a string)
      else if (value === null) {
        stringifiedParams[key] = undefined;
      }
      // Convert all other values to strings
      else {
        stringifiedParams[key] = String(value);
      }
    });

    // Add relation filters if any exist
    const relationFilters: Record<string, unknown> = {};
    if (hasAccountFilters) {
      relationFilters.account_filters = accountFilters;
    }
    if (hasTouchpointFilters) {
      relationFilters.touchpoint_filters = touchpointFilters;
    }

    const result: Record<string, string | undefined> = {
      ...stringifiedParams,
      extra_fields: combinedCustomFieldIds,
    };

    if (Object.keys(relationFilters).length > 0) {
      result.relation_filters = JSON.stringify(relationFilters);
    }

    return result;
  }, [
    getQueryParams,
    debouncedSearchQuery,
    columns,
    fieldIdsParam,
    newCustomFieldIdsParam,
    hasAccountFilters,
    hasTouchpointFilters,
    accountFilters,
    touchpointFilters,
  ]);

  // Use provided data source or fetch opportunities with infinite scrolling
  const defaultDataSource = useInfiniteOpportunities(
    dataSource ? undefined : queryParams,
  ) as InfiniteQueryObserverResult<InfiniteData<OpportunitiesResponse>>;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = dataSource || defaultDataSource;

  // Update isLoadingRef when isLoading changes
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  // Flatten the pages of data into a single array with memoization
  const opportunities = useMemo(() => {
    // Add early return for undefined data to avoid unnecessary processing
    if (!data || data.pages.length === 0) return [];

    // Use slice instead of flatMap if possible for better performance
    if (data.pages.length === 1) return data.pages[0].results;

    return data.pages.flatMap((page) => page.results) || [];
  }, [data]);

  // Total count of opportunities
  const totalOpportunities = data?.pages[0]?.total ?? 0;

  // Check if the metric limit is exceeded
  const isMetricLimitExceeded = totalOpportunities > OPPORTUNITY_METRIC_LIMIT;

  // Helper function for metric limit tooltip
  const getMetricLimitTooltipText = useCallback(() => {
    return `Apply additional filters to sort on metrics. For performance reasons, sorting on metrics is currently disabled when more than ${OPPORTUNITY_METRIC_LIMIT} opportunities match filters.`;
  }, []);

  // Function to display the sort reset toast notification
  const showSortResetToast = useCallback((columnTitle: string) => {
    const description = `For performance reasons, the sort on ${columnTitle} has been removed. Please filter to fewer than ${OPPORTUNITY_METRIC_LIMIT} opportunities and then try re-applying the sort.`;

    toast.warning("Table sort reset", {
      description,
      duration: 10000,
    });
  }, []);

  // Detect and handle metric sort warning from API
  // This effect responds to external API data, not internal state changes
  useEffect(() => {
    const firstPage = data?.pages[0];
    if (firstPage?.client_warning !== "SORT_ON_METRIC_TOO_MANY_MATCHING") {
      return;
    }

    // Check if we're sorting on a metric column that would trigger the warning
    if (!TOUCHPOINT_METRIC_COLUMNS.has(sortConfig.key)) {
      return;
    }

    // Capture the column name before resetting
    const columnToReset = sortConfig.key;
    const column = columns.find((col) => col.key === columnToReset);

    // Reset to default sort
    setSortConfig({ key: "close_date", direction: "desc" });
    clearFilter("sortColumn");
    clearFilter("sortDirection");

    // Show informative toast
    if (column?.title) {
      showSortResetToast(column.title);
    }
    // Note: We intentionally don't include sortConfig in dependencies
    // to avoid re-triggering when we update it above.
    // The guard (TOUCHPOINT_METRIC_COLUMNS.has) ensures we only reset once.
  }, [data?.pages, clearFilter, columns, showSortResetToast, sortConfig.key]);

  // Handle row click
  const handleRowClick = (opportunityId: string) => {
    if (customOnRowClick) {
      const opportunity = opportunities.find((o) => o.id === opportunityId);
      if (opportunity) {
        customOnRowClick(opportunity);
      }
    } else {
      setSelectedOpportunity(opportunityId);
      setIsSheetOpen(true);
    }
  };

  // Handle sort
  const handleSort = useCallback(
    (key: string) => {
      // Block metric sorting when limit exceeded
      const isMetricColumn = TOUCHPOINT_METRIC_COLUMNS.has(key);
      if (isMetricColumn && isMetricLimitExceeded) return;

      setSortConfig((prevConfig) => {
        const column = columns.find((col) => col.key === key);
        const newDirection: SortDirection =
          prevConfig.key === key
            ? prevConfig.direction === "asc"
              ? "desc"
              : "asc"
            : column?.defaultSortDirection || "asc";

        // Update filters in localStorage
        updateFilter("sortColumn", key);
        updateFilter("sortDirection", newDirection);

        // Reset the query to prevent duplicates
        void queryClient.resetQueries({ queryKey: ["opportunities"] });

        return { key, direction: newDirection };
      });
    },
    [columns, updateFilter, queryClient, isMetricLimitExceeded],
  );

  // Render sort icon function
  const renderSortIcon = useCallback(
    (column: UnifiedTableColumn<Opportunity>) => {
      if (!column?.sortable) return null;

      // Hide sort icon for metric columns when limit exceeded
      const key = column.key.toString();
      const isMetricColumn = TOUCHPOINT_METRIC_COLUMNS.has(key);
      if (isMetricColumn && isMetricLimitExceeded) {
        return null;
      }

      return (
        <SortIcon
          isActive={sortConfig.key === column.key}
          direction={
            sortConfig.key === column.key ? sortConfig.direction : null
          }
          showOnHover={false} // Don't show the icon on hover, only when active
        />
      );
    },
    [sortConfig, isMetricLimitExceeded],
  );

  // Function to handle double-click on column resize handle to auto-size
  const handleDoubleClickResize = useCallback(
    (_: MouseEvent, columnId: string) => {
      const resizeHandler = createColumnResizeHandler(
        setColumnSizing,
        "opportunities-table",
        {}, // Use default options
        undefined, // No callback needed for opportunities table
      );
      resizeHandler(_, columnId);
    },
    [setColumnSizing],
  );

  // Single column resize handler for kebab menu
  const handleSingleColumnResize = useCallback(
    createSingleColumnResizeHandler(setColumnSizing, "opportunities-table"),
    [],
  );

  // Bulk resize handler for autosize all button
  const handleBulkColumnResize = useCallback(
    createBulkColumnResizeHandler(setColumnSizing, "opportunities-table"),
    [],
  );

  // Handle removing a column from view
  const handleRemoveColumn = useCallback(
    (columnId: string) => {
      // Clear any filter on this column
      if (filters[columnId] !== undefined) {
        clearFilter(columnId);
      }

      // Reset sort if it was on this column (unless we're loading a saved view)
      if (sortConfig.key === columnId && !isLoadingView) {
        resetSortToDefaults();
        clearFilter("sortColumn");
        clearFilter("sortDirection");

        const columnTitle = columnTitleLookup[columnId];
        if (columnTitle) {
          toast.warning("Table sort reset", {
            description: `Sort on ${columnTitle} has been removed because the column was deselected.`,
            duration: 8000,
          });
        }
      }

      // Update column visibility
      const next: Record<string, boolean> = {
        ...(columnVisibility as Record<string, boolean>),
        [columnId]: false,
      };
      setColumnVisibility(next);
    },
    [
      setColumnVisibility,
      columnVisibility,
      filters,
      clearFilter,
      sortConfig,
      isLoadingView,
      resetSortToDefaults,
      columnTitleLookup,
    ],
  );

  // Modify table configuration to include columnOrder
  const table = useReactTable({
    data: opportunities,
    columns: columnDefs,
    state: {
      columnSizing,
      columnOrder,
    },
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    debugTable: false,
    defaultColumn: {
      minSize: 80,
      size: 150,
    },
  });

  // Render loading state
  const renderLoadingState = () => {
    // Using columnDefs now for structure during loading
    const headerGroups = table.getHeaderGroups();

    return (
      <>
        <TableHeader>
          {headerGroups.map((headerGroup) => (
            <TableRow key={headerGroup.id} className="bg-muted/50">
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} style={{ width: header.getSize() }}>
                  <Skeleton className="h-4 w-full" />
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className="bg-card">
          {Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={i}>
              {/* Use header length for placeholders, assuming same number of cells */}
              {headerGroups[0].headers.map((header) => (
                <TableCell key={`${i}-${header.id}`}>
                  <Skeleton className="h-4 w-full" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </>
    );
  };

  // Memoize pinnedColumnPositions calculation at component level to avoid React hooks rules violation
  const pinnedColumnPositions = useMemo(() => {
    const positions: Record<string, number> = {};
    let cumulativeLeft = 0;

    columnOrder.forEach((colId) => {
      if (pinnedColumns.includes(colId)) {
        positions[colId] = cumulativeLeft;
        const column = table.getColumn(colId);
        if (column) {
          cumulativeLeft += column.getSize();
        }
      }
    });

    return positions;
  }, [columnOrder, pinnedColumns, table]);

  // Render table content
  const renderTableContent = () => {
    const virtualRows = rowVirtualizer.getVirtualItems();
    const totalSize = rowVirtualizer.getTotalSize();

    return (
      <>
        <DraggableTableHeader
          table={table}
          columnOrder={columnOrder}
          handleSort={handleSort}
          sortConfig={sortConfig}
          renderSortIcon={renderSortIcon}
          filters={filters}
          updateFilter={updateFilter}
          clearFilter={clearFilter}
          columns={columns}
          pinnedColumns={pinnedColumns}
          onTogglePin={toggleColumnPin}
          wrappedColumns={wrappedColumns}
          onToggleWrap={toggleColumnWrap}
          handleDoubleClickResize={handleDoubleClickResize}
          onResizeToFit={handleSingleColumnResize}
          isMetricLimitExceeded={isMetricLimitExceeded}
          getMetricLimitTooltipText={getMetricLimitTooltipText}
          onRemoveColumn={handleRemoveColumn}
        />
        <TableBody
          className="bg-card"
          style={{ height: totalSize, position: "relative" }}
        >
          {isError ? (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center text-muted-foreground"
              >
                Error loading opportunities:{" "}
                {error instanceof Error ? error.message : String(error)}
              </TableCell>
            </TableRow>
          ) : opportunities.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center text-muted-foreground"
              >
                No matching opportunities
              </TableCell>
            </TableRow>
          ) : (
            <>
              {virtualRows.map((virtualRow: VirtualItem) => {
                const row = table.getRowModel().rows[virtualRow.index];
                return (
                  <TableRow
                    ref={
                      rowVirtualizer.measureElement as React.Ref<HTMLTableRowElement>
                    }
                    key={row.id}
                    data-index={virtualRow.index}
                    style={{
                      transform: `translateY(${virtualRow.start}px)`,
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      display: "flex",
                    }}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => handleRowClick(row.original.id)}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const isPinned = pinnedColumns.includes(cell.column.id);
                      const isWrapped = wrappedColumns.includes(cell.column.id);
                      const pinnedLeft = pinnedColumnPositions[cell.column.id];

                      return (
                        <TableCell
                          key={cell.id}
                          style={{
                            width: cell.column.getSize(),
                            minWidth: cell.column.getSize(),
                            maxWidth: cell.column.getSize(),
                            ...(isPinned && { left: `${pinnedLeft}px` }),
                          }}
                          className={cn(
                            "text-xs",
                            cell.column.id === sortConfig.key && "bg-muted/40",
                            isPinned && "pinned-column bg-background",
                          )}
                          data-column-id={cell.column.id}
                        >
                          <div
                            className={cn(
                              "cell-content",
                              isWrapped && "wrapped",
                            )}
                            title={String(row.getValue(cell.column.id) || "")}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
              {/* Load more trigger element */}
              <TableRow ref={loadMoreRef}>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className={cn("h-12", !hasNextPage && "hidden")}
                >
                  {!isFetchingNextPage && hasNextPage ? "Load more" : null}
                </TableCell>
              </TableRow>
            </>
          )}
        </TableBody>
      </>
    );
  };

  // Setup for CSV export
  const { exportOpportunitiesAsync } = useOpportunitiesExport();

  // Set up paginated CSV export
  const exportAllData = useMemo<() => Promise<Opportunity[]>>(() => {
    if (onExportCSV) {
      return async () => {
        const rows = await onExportCSV();
        return rows as unknown as Opportunity[];
      };
    }
    return () => exportOpportunitiesAsync(queryParams);
  }, [onExportCSV, exportOpportunitiesAsync, queryParams]);

  // Filter columns for CSV export based on visibility
  const visibleColumnsForExport = useMemo(() => {
    return columns.filter((col) => {
      const colId = col.key.toString();

      // Check if it's a custom field
      if (col.isCustomField) {
        return selectedCustomColumns.some((c) => c.id === colId);
      }

      // For required columns, always include them (matching table display behavior)
      // This ensures consistency with the table which always shows required columns
      if (col.required === true) {
        return true;
      }

      // For columns with defaultHidden: true, only include if explicitly visible
      if (col.defaultHidden === true) {
        return columnVisibility[colId] === true;
      }

      // For other core columns, check visibility state
      // Include if not explicitly hidden (handles uninitialized state)
      return columnVisibility[colId] !== false;
    });
  }, [columns, columnVisibility, selectedCustomColumns]);

  const { exportData, isExporting, defaultFilename } =
    usePaginatedCSVExport<Opportunity>({
      fetchAllData: exportAllData,
      columns: visibleColumnsForExport,
      filename: `opportunities_${formatDateForFilename(new Date())}`,
    });

  // State for export filename dialog
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  const handleExport = (filename: string) => {
    exportData(filename);
  };

  const rowModel = table.getRowModel();

  const hasWrappedColumns = wrappedColumns.length > 0;
  const rowVirtualizer = useVirtualizer({
    count: rowModel.rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: hasWrappedColumns ? () => 60 : () => 36,
    overscan: 10,
    measureElement: hasWrappedColumns
      ? (el) => el?.getBoundingClientRect().height ?? 60
      : undefined,
  });

  // Table refs and state
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const calculateTableWidthRef = useRef<() => number>();
  const isLoadingRef = useRef(isLoading);

  // Make sure we initialize the calculation function after table is created and columnDefs is available
  useEffect(() => {
    if (columnDefs.length === 0) return;

    calculateTableWidthRef.current = () => {
      return columnDefs.reduce(
        (total, col) =>
          total + (columnSizing[col.id!] || (col.size as number) || 150),
        0,
      );
    };
  }, [columnDefs, columnSizing]);

  // Load more data when the load more element is in view
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Search functions are now provided by useSearchState hook

  return (
    <>
      <Card className="h-full relative flex flex-col">
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Filter by opportunity name..."
                className="w-[250px] border-0 border-b border-border shadow-none rounded-none pl-7 pr-8 h-10 placeholder:text-muted-foreground focus:ring-0 focus-visible:ring-0 focus:border-muted-foreground transition-colors bg-transparent hover:border-muted-foreground"
                onChange={(e) => handleSearchChange(e.target.value)}
                value={searchInputValue}
              />
              {searchInputValue && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent"
                  onClick={clearSearch}
                >
                  <X className="h-4 w-4 text-muted-foreground hover:text-foreground/80" />
                </Button>
              )}
            </div>
            <InfoTooltip
              trigger={
                <button
                  className="ml-2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Wildcard search info"
                  onClick={(e) => e.stopPropagation()}
                >
                  <InfoCircledIcon className="h-3.5 w-3.5" />
                </button>
              }
              side="right"
              sideOffset={5}
            >
              <div className="text-sm space-y-2">
                <p className="font-medium">Supports wildcards</p>
                <ul className="space-y-1">
                  <li>
                    <code className="px-1 py-0.5 bg-muted rounded text-xs">
                      _
                    </code>{" "}
                    matches a single character
                  </li>
                  <li>
                    <code className="px-1 py-0.5 bg-muted rounded text-xs">
                      %
                    </code>{" "}
                    matches zero or more characters
                  </li>
                </ul>
              </div>
            </InfoTooltip>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-sm text-muted-foreground">
              {isLoading
                ? "Loading..."
                : totalOpportunities > 0
                  ? `${totalOpportunities.toLocaleString()} matching ${
                      totalOpportunities === 1 ? "opportunity" : "opportunities"
                    }`
                  : "No matching opportunities"}
            </div>

            {/* Run Analyses button - only when not in DRR context */}
            {!dataSource && (
              <RunAnalysesPopover
                totalCount={totalOpportunities}
                queryParams={queryParams}
                isLoading={isLoading}
              />
            )}

            {rightToolbarActions}

            <OpportunityColumnSelector
              columnState={
                stateOverride
                  ? {
                      availableColumns: columnState.availableColumns,
                      isColumnSelected: columnState.isColumnSelected,
                      toggleColumn: columnState.toggleColumn,
                      isLoading: columnState.isLoading,
                      error: columnState.error,
                    }
                  : undefined
              }
            />

            {/* Relation Filters Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilterModal(true)}
              className="flex items-center gap-1.5"
              data-testid="relation-filters-button"
            >
              <Filter
                className={`h-4 w-4 ${
                  hasAccountFilters || hasTouchpointFilters
                    ? "text-primary"
                    : "text-muted-foreground"
                }`}
              />
              <span>Relation Filters</span>
              {(hasAccountFilters || hasTouchpointFilters) && (
                <Badge className="ml-1 bg-primary text-primary-foreground">
                  {(accountFilters?.filters?.length || 0) +
                    (touchpointFilters?.filters?.length || 0)}
                </Badge>
              )}
            </Button>

            {/* Autosize All Columns Button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    const visibleColumnIds = columnOrder.filter(
                      (columnId) =>
                        (columnVisibility as Record<string, boolean>)[
                          columnId
                        ] !== false,
                    );
                    handleBulkColumnResize(visibleColumnIds);
                  }}
                  className="h-8 w-8"
                >
                  <UnfoldHorizontal className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Resize all visible columns to fit their content</p>
              </TooltipContent>
            </Tooltip>

            {/* Reset Table Menu */}
            <ResetTableMenu
              onResetWidths={resetColumnWidths}
              onResetOrder={resetColumnOrder}
              onResetColumns={resetColumnsToDefault}
              onResetTableFilters={handleClearAllFilters}
              onResetPinnedColumns={resetPinnedColumns}
              onResetSort={resetSortOrder}
              onResetWrappedColumns={resetWrappedColumns}
              onResetAll={handleResetTable}
            />

            {/* CSV Export Button */}
            <ExportFilenameDialog
              open={isExportDialogOpen}
              onOpenChange={setIsExportDialogOpen}
              defaultFilename={defaultFilename}
              extension="csv"
              onExport={handleExport}
              disabled={isExporting || opportunities.length === 0}
            >
              <CSVExportButton
                onClick={() => setIsExportDialogOpen(true)}
                isExporting={isExporting}
                disabled={isExporting || opportunities.length === 0}
                tooltipText="Export opportunities data to CSV"
              />
            </ExportFilenameDialog>
          </div>
        </div>

        {/* Active Filters Display – consistent height */}
        <div className="px-4 pb-2 flex items-center justify-between min-h-8 flex-shrink-0">
          {hasFilters ? (
            <ActiveFiltersDisplay
              filters={filters}
              columns={columns}
              clearFilter={clearFilter}
              clearAllFilters={handleClearAllFilters}
              clearAllText="Clear all table filters"
              filterType="table"
            />
          ) : (
            <div></div> /* Empty div to maintain flex layout */
          )}
        </div>
        {/* Relation filters chips */}
        {Object.keys(relationDisplayFilters).length > 0 && (
          <div className="px-4 pb-2 flex items-center justify-between">
            <ActiveFiltersDisplay
              filters={relationDisplayFilters}
              columns={relationFilterColumns}
              clearFilter={handleClearRelationFilter}
              clearAllFilters={clearRelationFilters}
              clearAllText="Clear all relation filters"
              filterType="relation"
            />
          </div>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={constrainedClosestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToTableBounds]}
        >
          <div
            ref={tableContainerRef}
            className={cn(
              "rounded-md border overflow-y-auto flex-grow",
              table.getState().columnSizingInfo.isResizingColumn &&
                "table-resizing",
            )}
            style={{
              maxWidth: isMobile
                ? "calc(100vw - 2rem)"
                : state === "collapsed"
                  ? "calc(100vw - var(--sidebar-width-icon) - 2rem)"
                  : "calc(100vw - var(--sidebar-width) - 2rem)",
              minWidth: "640px", // Prevent table from becoming too narrow on small screens
            }}
          >
            <Table
              className="w-full table-fixed"
              style={{ width: table.getTotalSize() }}
              data-table-id="opportunities-table"
              data-table-container
            >
              {isLoading ? renderLoadingState() : renderTableContent()}
            </Table>
          </div>
        </DndContext>
      </Card>

      {/* Fixed position loading indicator */}
      {isFetchingNextPage && (
        <div className="fixed bottom-10 left-1/2 transform -translate-x-1/2 z-10 bg-background/90 shadow-md rounded-md px-4 py-2 border">
          <div className="flex items-center justify-center">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="ml-2">Loading more...</span>
          </div>
        </div>
      )}

      <OpportunityDetailsSheet
        opportunity={
          selectedOpportunity
            ? opportunities.find((o) => o.id === selectedOpportunity) || null
            : null
        }
        isOpen={isSheetOpen}
        onOpenChange={setIsSheetOpen}
      />

      <EntityFilterModal
        open={showFilterModal}
        onOpenChange={setShowFilterModal}
        scope="opportunities::list"
        entity="opportunity"
      />
    </>
  );
}
